import {
  Button,
  createSortableColumn,
  DataTable,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  type TanstackReactTable,
} from "@devhop/ui";
import {
  ClipboardCheck,
  Eye,
  FileDown,
  MoreVertical,
  Pencil,
  Trash2,
} from "lucide-react";
import type { MeetingListItem } from "@/modules/management/meeting-minutes/domain/types";
import { formatDateLocal } from "@/shared/lib/date-time";

type MeetingTableProps = {
  data: MeetingListItem[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onView?: (row: MeetingListItem) => void;
  onEdit?: (row: MeetingListItem) => void;
  onPdfPreview?: (row: MeetingListItem) => void;
  onViewActions?: (row: MeetingListItem) => void;
  onDelete?: (row: MeetingListItem) => void;
};

export function MeetingTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onView,
  onEdit,
  onPdfPreview,
  onViewActions,
  onDelete,
}: MeetingTableProps) {
  const columns: TanstackReactTable.ColumnDef<MeetingListItem>[] = [
    createSortableColumn<MeetingListItem>("meetingDate", "ວັນທີ", {
      size: 110,
      cell: ({ row }) => formatDateLocal(row.original.meetingDate),
    }),
    createSortableColumn<MeetingListItem>("title", "ຫົວຂໍ້", {
      size: 240,
      cell: ({ row }) => (
        <div>
          {onView ? (
            <button
              type="button"
              className="font-medium text-primary hover:underline"
              onClick={() => onView(row.original)}
            >
              {row.original.title}
            </button>
          ) : (
            row.original.title
          )}
        </div>
      ),
    }),
    createSortableColumn<MeetingListItem>("createdByName", "ຜູ້ສ້າງ", {
      size: 140,
      cell: ({ row }) => row.original.createdByName ?? "—",
    }),
    {
      accessorKey: "attendeeCount",
      header: "ຜູ້ຮ່ວມ",
      size: 80,
      cell: ({ row }) => row.original.attendeeCount,
    },
    {
      accessorKey: "actionCount",
      header: "ປະເດັນ",
      size: 80,
      cell: ({ row }) => row.original.actionCount,
    },
    {
      id: "actions",
      header: "",
      size: 56,
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {onView && (
              <DropdownMenuItem onClick={() => onView(row.original)}>
                <Eye className="mr-2 h-4 w-4" />
                ເບິ່ງ
              </DropdownMenuItem>
            )}
            {onEdit && (
              <DropdownMenuItem onClick={() => onEdit(row.original)}>
                <Pencil className="mr-2 h-4 w-4" />
                ແກ້ໄຂ
              </DropdownMenuItem>
            )}
            {onPdfPreview && (
              <DropdownMenuItem onClick={() => onPdfPreview(row.original)}>
                <FileDown className="mr-2 h-4 w-4" />
                ເບິ່ງ PDF
              </DropdownMenuItem>
            )}
            {onViewActions && (
              <DropdownMenuItem onClick={() => onViewActions(row.original)}>
                <ClipboardCheck className="mr-2 h-4 w-4" />
                ປະເດັນ
              </DropdownMenuItem>
            )}
            {onDelete && (
              <DropdownMenuItem
                onClick={() => onDelete(row.original)}
                className="text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                ລຶບ
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={data}
      isLoading={isLoading}
      totalCount={totalCount}
      offset={offset}
      limit={limit}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (!sorting[0] || !sorting[0].id || sorting[0].id === "") return;
        onSortingChange(sorting[0].id as string, !!sorting[0].desc);
      }}
    />
  );
}
